/**
 * Module alertes : liste ouvertes/closes, action de clôture selon les droits.
 *
 * `05-api-backend.md` : « seule la clôture est possible ». Rouvrir une alerte n'est jamais une action
 * utilisateur — une condition encore vraie en rouvre une nouvelle au prochain cycle, avec sa propre
 * date d'ouverture. Il n'y a donc pas de bouton "rouvrir".
 */
import { useMemo, useState } from "react";

import { useIdentite } from "../../shared/auth/contexte";
import { Chargement, Erreur, Vide } from "../../shared/composants/Etats";
import {
  collecterToutesLesPages,
  horodatageFichier,
  telechargerCsv,
} from "../../shared/composants/exportCsv";
import { formaterDate } from "../../shared/composants/formatage";
import type { Alerte } from "../../shared/api/types";
import { AlertCard, LIBELLE_TYPE } from "./AlertCard";
import { useAlertes, useCloturerAlerte } from "./requetes";

const LIBELLE_PRIORITE: Record<string, string> = {
  normale: "Normale",
  moyenne: "Moyenne",
  haute: "Haute",
};

const LIBELLE_STATUT: Record<string, string> = {
  ouverte: "Ouverte",
  relancee: "Relancée",
  close: "Close",
};

type ModePeriode = "illimite" | "derniers_jours" | "plage";

function ilYAJours(jours: number): string {
  const date = new Date();
  date.setDate(date.getDate() - jours);
  return date.toISOString().slice(0, 10);
}

export function EcranAlertes() {
  const identite = useIdentite();
  const [statut, definirStatut] = useState<"ouverte" | "close">("ouverte");
  const [filtreType, definirFiltreType] = useState("");
  const requete = useAlertes(statut);
  const cloturer = useCloturerAlerte();

  const [modePeriode, definirModePeriode] = useState<ModePeriode>("illimite");
  const [derniersJours, definirDerniersJours] = useState("30");
  const [debut, definirDebut] = useState(() => ilYAJours(30));
  const [fin, definirFin] = useState(() => ilYAJours(0));
  const [exportEnCours, definirExportEnCours] = useState(false);
  const [erreurExport, definirErreurExport] = useState<string | null>(null);

  const peutTraiter =
    identite.profil === "admin-totalenergies" || identite.profil === "operationnel-totalenergies";

  const alertes = useMemo(() => requete.data?.data ?? [], [requete.data]);
  const typesPresents = useMemo(
    () => Array.from(new Set(alertes.map((a) => a.type_alerte))).sort(),
    [alertes],
  );
  const alertesFiltrees = filtreType ? alertes.filter((a) => a.type_alerte === filtreType) : alertes;

  // Groupées par type plutôt qu'en une seule liste continue : une trentaine d'alertes de huit types
  // différents, empilées sans distinction, ne laisse identifier ni les catégories ni les anomalies.
  const groupes = useMemo(() => {
    const parType = new Map<string, typeof alertesFiltrees>();
    for (const alerte of alertesFiltrees) {
      const groupe = parType.get(alerte.type_alerte) ?? [];
      groupe.push(alerte);
      parType.set(alerte.type_alerte, groupe);
    }
    return Array.from(parType.entries()).sort(([, a], [, b]) => b.length - a.length);
  }, [alertesFiltrees]);

  async function exporter() {
    definirErreurExport(null);
    definirExportEnCours(true);
    try {
      const periode =
        modePeriode === "derniers_jours"
          ? { debut: ilYAJours(Number(derniersJours) || 0), fin: ilYAJours(0) }
          : modePeriode === "plage"
            ? { debut, fin }
            : { debut: null, fin: null };

      const alertesExportees = await collecterToutesLesPages<Alerte>("/v1/alertes", {
        "filter[statut]": statut,
        "filter[type]": filtreType || null,
        "filter[debut]": periode.debut,
        "filter[fin]": periode.fin,
        pageSize: 200,
      });

      telechargerCsv(
        `alertes-${statut}-${horodatageFichier()}.csv`,
        [
          { libelle: "Date/heure", valeur: (a: Alerte) => formaterDate(a.date_ouverture) },
          { libelle: "GRV", valeur: (a: Alerte) => a.numero_grv },
          { libelle: "Tracker concerné", valeur: (a: Alerte) => a.numero_serie_tracker ?? null },
          { libelle: "Client", valeur: (a: Alerte) => a.client_id ?? null },
          {
            libelle: "Type d'alerte",
            valeur: (a: Alerte) => LIBELLE_TYPE[a.type_alerte] ?? a.type_alerte,
          },
          { libelle: "Priorité", valeur: (a: Alerte) => LIBELLE_PRIORITE[a.priorite] ?? a.priorite },
          { libelle: "Statut", valeur: (a: Alerte) => LIBELLE_STATUT[a.statut] ?? a.statut },
          { libelle: "Description", valeur: (a: Alerte) => a.message },
          {
            libelle: "Date de prise en compte",
            valeur: (a: Alerte) => formaterDate(a.date_cloture),
          },
          { libelle: "Date de clôture", valeur: (a: Alerte) => formaterDate(a.date_cloture) },
          {
            libelle: "Utilisateur ayant traité/clôturé",
            valeur: (a: Alerte) => a.cloture_par ?? null,
          },
        ],
        alertesExportees,
      );
    } catch (erreur) {
      definirErreurExport(erreur instanceof Error ? erreur.message : "L'export a échoué.");
    } finally {
      definirExportEnCours(false);
    }
  }

  return (
    <section>
      <h1>Alertes</h1>

      <div className="barre-de-filtres">
        <div role="tablist" aria-label="Filtre par statut">
          <button
            type="button"
            aria-pressed={statut === "ouverte"}
            onClick={() => definirStatut("ouverte")}
          >
            Ouvertes
          </button>
          <button
            type="button"
            aria-pressed={statut === "close"}
            onClick={() => definirStatut("close")}
          >
            Closes
          </button>
        </div>

        <label htmlFor="filtre-alerte-type">Type</label>
        <select
          id="filtre-alerte-type"
          value={filtreType}
          onChange={(e) => definirFiltreType(e.target.value)}
        >
          <option value="">Tous les types</option>
          {typesPresents.map((type) => (
            <option key={type} value={type}>
              {LIBELLE_TYPE[type] ?? type}
            </option>
          ))}
        </select>

        <label htmlFor="mode-periode-export-alertes">Période à exporter</label>
        <select
          id="mode-periode-export-alertes"
          value={modePeriode}
          onChange={(e) => definirModePeriode(e.target.value as ModePeriode)}
        >
          <option value="illimite">Illimitée</option>
          <option value="derniers_jours">Derniers jours</option>
          <option value="plage">Du / au</option>
        </select>

        {modePeriode === "derniers_jours" && (
          <>
            <label htmlFor="derniers-jours-export-alertes">Nombre de jours</label>
            <input
              id="derniers-jours-export-alertes"
              type="number"
              min="1"
              step="1"
              value={derniersJours}
              onChange={(e) => definirDerniersJours(e.target.value)}
            />
          </>
        )}

        {modePeriode === "plage" && (
          <>
            <label htmlFor="export-alertes-debut">Du</label>
            <input
              id="export-alertes-debut"
              type="date"
              value={debut}
              max={fin}
              onChange={(e) => definirDebut(e.target.value)}
            />
            <label htmlFor="export-alertes-fin">Au</label>
            <input
              id="export-alertes-fin"
              type="date"
              value={fin}
              min={debut}
              max={ilYAJours(0)}
              onChange={(e) => definirFin(e.target.value)}
            />
          </>
        )}

        <button type="button" onClick={() => void exporter()} disabled={exportEnCours}>
          {exportEnCours ? "Export en cours…" : "Exporter (Excel)"}
        </button>
      </div>

      {erreurExport && <p role="alert" className="formulaire-ligne__erreur">{erreurExport}</p>}

      {requete.isPending && <Chargement libelle="Chargement des alertes…" />}
      {requete.isError && <Erreur erreur={requete.error} reessayer={() => void requete.refetch()} />}
      {requete.data && alertesFiltrees.length === 0 && (
        <Vide
          libelle={
            filtreType
              ? "Aucune alerte ne correspond à ce type."
              : statut === "ouverte"
                ? "Aucune alerte ouverte."
                : "Aucune alerte close."
          }
        />
      )}

      {groupes.map(([type, alertesDuType]) => (
        <div key={type} className="alertes__groupe">
          <h2 className="alertes__groupe-titre">
            {LIBELLE_TYPE[type] ?? type}
            <span className="badge">{alertesDuType.length}</span>
          </h2>
          <div className="liste-alertes">
            {alertesDuType.map((alerte) => (
              <AlertCard
                key={alerte.alert_id}
                alerte={alerte}
                peutTraiter={peutTraiter}
                enCoursDeCloture={
                  cloturer.isPending && cloturer.variables?.alertId === alerte.alert_id
                }
                cloturer={() =>
                  cloturer.mutate({ alertId: alerte.alert_id, corps: { statut: "close" } })
                }
              />
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
